import React, { useState, useEffect } from 'react';
import { X, Settings as SettingsIcon, Check, LayoutList } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { Json } from '../lib/database.types';
import { useTranslation } from '../lib/LanguageContext';

interface ColumnSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  onColumnsChange: (columns: ColumnSettings) => void;
  initialSettings: ColumnSettings | null;
}

export interface ColumnSettings {
  backlog: boolean;
  in_progress: boolean;
  review: boolean;
  testing: boolean;
  done: boolean;
}

export function ColumnSettings({ isOpen, onClose, onColumnsChange, initialSettings }: ColumnSettingsProps) {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<ColumnSettings>({
    backlog: true,
    in_progress: true,
    review: false,
    testing: false,
    done: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize settings from props
  useEffect(() => {
    if (initialSettings) {
      setSettings(initialSettings);
    }
  }, [initialSettings]);

  const handleToggle = (column: keyof ColumnSettings) => {
    // Don't allow toggling off the required columns
    if (column === 'backlog' || column === 'in_progress' || column === 'done') {
      return;
    }

    setSettings(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      // Save settings to database
      const { data, error: saveError } = await supabase.rpc('update_user_settings', {
        new_settings: {
          columns: settings
        } as unknown as Json
      });

      if (saveError) {
        throw saveError;
      }

      // Notify parent component
      onColumnsChange(settings);
      onClose();
    } catch (err) {
      console.error('Error saving column settings:', err);
      setError(t('failedToSaveSettings'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;
  
  // Order of columns to display in the UI
  const columnOrder: (keyof ColumnSettings)[] = ['backlog', 'in_progress', 'review', 'testing', 'done'];

  // Function to get the display name for a column
  const getColumnDisplayName = (column: keyof ColumnSettings): string => {
    switch(column) {
      case 'backlog': return t('backlog');
      case 'in_progress': return t('inProgress');
      case 'review': return t('review');
      case 'testing': return t('testing');
      case 'done': return t('done');
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center border-b px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900">{t('columnSettings')}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="px-6 py-4">
          <p className="text-sm text-gray-600 mb-4">
            {t('chooseColumns')}
          </p>
          
          <div className="space-y-3">
            {columnOrder.map(column => (
              <div 
                key={column} 
                className="flex items-center justify-between p-3 rounded-lg border border-gray-200"
              >
                <div className="font-medium">
                  {getColumnDisplayName(column)}
                </div>
                
                <div className="flex items-center">
                  <button
                    onClick={() => handleToggle(column)}
                    disabled={column === 'backlog' || column === 'in_progress' || column === 'done'}
                    className={`
                      w-12 h-6 rounded-full flex items-center transition-colors duration-200 focus:outline-none
                      ${settings[column] ? 'bg-indigo-600 justify-end' : 'bg-gray-300 justify-start'}
                      ${column === 'backlog' || column === 'in_progress' || column === 'done' ? 'opacity-60 cursor-not-allowed' : ''}
                    `}
                  >
                    <span className={`bg-white w-5 h-5 rounded-full shadow-md transform transition-transform duration-200 mx-0.5 flex items-center justify-center ${settings[column] ? 'text-indigo-600' : 'text-gray-500'}`}>
                      {settings[column] && <Check className="h-3 w-3" />}
                    </span>
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          {error && (
            <div className="mt-4 text-sm text-red-600 bg-red-50 p-3 rounded">
              {error}
            </div>
          )}
        </div>
        
        <div className="border-t px-6 py-4 flex justify-end space-x-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? t('saving') : t('saveChanges')}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ColumnSettingsButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="text-indigo-100 hover:text-white focus:outline-none flex items-center"
      title="Column Settings"
    >
      <LayoutList className="h-5 w-5" />
    </button>
  );
}